import React from 'react';
import type {FetchTaskResponse, Task} from '@rocket-ui/lk-api';
import {getTaskPrice, getTaskStatusLabel} from 'entities/task';
import {DraftTaskActions} from 'features/draft-task-actions';
import {
	isFreeSmzReceiptChecking,
	isFreeSmzReceiptError,
	readManualFnsReceiptFields,
	RECEIPT_CHECKING_LABEL,
	RECEIPT_CHECKING_STATUS_LABEL,
	RECEIPT_ERROR_LABEL,
	useOrderActions,
} from 'features/order-actions';
import {ProfileAlertsBanner} from 'features/profile';
import {ErrorBanner, ErrorBannerWithDrawer} from 'shared/ui';
import {DetailsSection} from './DetailsSection';
import {DocumentsSection} from './Document/DocumentsSection';
import {OrderPageHeader} from './OrderPageHeader';
import {ReceiptCheckingBanner} from './ReceiptCheckingBanner';
import {ServiceItem, ServicesSection} from './Section/ServicesSection';
import styles from './OrderPage.module.css';

function formatTaskDate(date?: string | null): string | null {
	if (!date) return null;

	const parsedDate = new Date(date);
	if (Number.isNaN(parsedDate.getTime())) return null;

	return new Intl.DateTimeFormat('ru-RU', {
		day: 'numeric',
		month: 'long',
		hour: '2-digit',
		minute: '2-digit',
	}).format(parsedDate);
}

function getTaskContractDeadline(task: FetchTaskResponse): string | null {
	if (task.periodTo) return task.periodTo;

	const {createdAt, periodDuration} = task;
	if (!createdAt || periodDuration == null) return null;

	const start = new Date(createdAt);
	if (Number.isNaN(start.getTime())) return null;

	const deadline = new Date(start);
	deadline.setDate(deadline.getDate() + periodDuration);

	return deadline.toISOString();
}

function getInitials(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean);

	if (parts.length === 0) return '—';

	return parts
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase() ?? '')
		.join('');
}

function formatRublesFromCents(cents?: number | null): string {
	if (cents == null) return '—';
	return `${(cents / 100).toLocaleString('ru-RU')} ₽`;
}

function formatServiceQuantity(quantity: unknown): string {
	if (quantity == null) return '—';
	if (typeof quantity === 'number' && !Number.isNaN(quantity)) {
		return String(quantity);
	}
	return '—';
}

export type OrderProps = {
	task: FetchTaskResponse;
};

export function Order({task}: OrderProps): React.ReactElement {
	const receiptFields = readManualFnsReceiptFields(task);
	const hasReceiptError = isFreeSmzReceiptError(receiptFields);
	const isReceiptChecking = isFreeSmzReceiptChecking(receiptFields);
	const taskForActions = {
		id: task.id,
		status: task.status,
		cancellable: task.cancellable,
		acceptanceRequired: task.acceptanceRequired,
		contractCertificateUri: task.contractCertificateUri,
		acceptanceUri: task.acceptanceUri,
		acceptanceCertificateUri: task.acceptanceCertificateUri,
		receiptUri: task.receiptUri,
		offlineReceiptUri: task.offlineReceiptUri,
		...receiptFields,
	};
	const {actions, signAcceptance, createReceipt, declineDrawer, employmentMismatch} = useOrderActions(taskForActions);

	const statusLabel = hasReceiptError
		? RECEIPT_ERROR_LABEL
		: isReceiptChecking
			? RECEIPT_CHECKING_STATUS_LABEL
			: getTaskStatusLabel(task.status);
	const createdAt = formatTaskDate(task.createdAt);
	const metaSubtitle = [createdAt, getTaskPrice(task)].filter(Boolean).join(' · ');
	const metaFull = [statusLabel, metaSubtitle].filter(Boolean).join(' · ');

	const services: ServiceItem[] =
		task.services?.map((service, index) => {
			const serviceOrder = service.order ?? index + 1;
			const descriptionParts = [service.description, service.location].filter(Boolean);
			const totalPrice = formatRublesFromCents(service.totalPriceCents);
			const unitPrice = formatRublesFromCents(service.unitPriceCents);

			return {
				id: `${serviceOrder}-${index}`,
				title: `Услуга #${serviceOrder}`,
				description: descriptionParts.join('. ') || 'Описание отсутствует',
				price: totalPrice,
				quantity: formatServiceQuantity(service.quantity),
				unit: service.unit?.trim() || '—',
				unitPrice,
				totalCost: totalPrice,
				drawerDescription: service.description?.trim() || '—',
				address: service.location?.trim() || '—',
			};
		}) ?? [];

	const documents = [
		{label: 'Договор', uri: task.contractUri},
		{label: 'Сертификат договора', uri: task.contractCertificateUri},
		{label: 'Акт', uri: task.acceptanceUri},
		{label: 'Сертификат акта', uri: task.acceptanceCertificateUri},
		{label: 'Печатная форма', uri: task.printedFormUri},
		{label: 'Сертификат печатной формы', uri: task.printedFormCertificateUri},
		{label: 'Чек', uri: task.receiptUri},
		{label: 'Оффлайн чек', uri: task.offlineReceiptUri},
	].filter((document) => Boolean(document.uri)) as {label: string; uri: string}[];

	const customerName = task.customerName ?? 'Не указан';
	const deadlineValue = formatTaskDate(getTaskContractDeadline(task)) ?? 'Не указан';

	return (
		<>
			<OrderPageHeader
				id={task.id || 0}
				metaFull={metaFull}
				metaSubtitle={metaSubtitle}
				statusLabel={statusLabel}
				acceptanceUri={task.acceptanceUri}
				acceptanceCertificateUri={task.acceptanceCertificateUri}
				type={task.type as unknown as Task['type']}
				signAcceptance={signAcceptance}
				createReceipt={createReceipt}
				employmentMismatch={employmentMismatch}
				declineDrawer={declineDrawer}
			/>

			<div className={actions.length ? `${styles.page} ${styles.pageWithMobileActions}` : styles.page}>
				<ProfileAlertsBanner />
				{task.status === 'error' && <ErrorBannerWithDrawer text={task.errorText} okButtonText="Окей" />}
				{hasReceiptError ? (
					<button
						type="button"
						onClick={createReceipt.open}
						className="w-full text-left focus:outline-none"
					>
						<ErrorBanner text={RECEIPT_ERROR_LABEL} className="cursor-pointer focus-visible:ring-2" />
					</button>
				) : isReceiptChecking ? (
					<ReceiptCheckingBanner text={RECEIPT_CHECKING_LABEL} />
				) : null}
				<ServicesSection services={services} />
				<DocumentsSection documents={documents} />
				<DetailsSection
					customerInitials={getInitials(customerName)}
					customerName={customerName}
					deadlineLabel="Срок выполнения по договору"
					deadlineValue={deadlineValue}
				/>
				<DraftTaskActions actions={actions} />
			</div>
		</>
	);
}
